import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Microscope,
  FlaskConical,
  LineChart,
  Search,
  BookOpen,
  ClipboardCheck,
  Layers,
  Sparkles,
  ArrowRight,
  Zap,
  Thermometer,
  Activity,
  Waves,
  Award,
  GraduationCap,
} from "lucide-react";
import { PageHero, CTASection } from "@/components/Bits";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";

const VIDEOS = [
  { id: "GRS4YgO17sw", label: "Data acquisition in progress · Vernier sensor",   short: true  },
  { id: "4fqTmpIIzgk", label: "Live measurement · student in action",             short: true  },
  { id: "T9Sg3Uf2Z3o", label: "Experimental setup · precision instrumentation",   short: false },
  { id: "skjsIZeU6oI", label: "Data analysis · from graph to inference",          short: false },
  { id: "k0EviptnLrs", label: "Research process · complete cycle",                short: false },
];

const RESEARCH_CYCLE = [
  { icon: Search,          hex: "#8B5CF6", t: "Identify a research problem" },
  { icon: BookOpen,        hex: "#2E6BB0", t: "Review scientific literature" },
  { icon: Sparkles,        hex: "#F0A831", t: "Formulate questions & hypotheses" },
  { icon: FlaskConical,    hex: "#1E9E8A", t: "Design experimental procedures" },
  { icon: Microscope,      hex: "#E14C7A", t: "Collect accurate experimental data" },
  { icon: LineChart,       hex: "#8B5CF6", t: "Perform statistical & graphical analysis" },
  { icon: Layers,          hex: "#2E6BB0", t: "Interpret scientific evidence" },
  { icon: ClipboardCheck,  hex: "#1E9E8A", t: "Discuss limitations & sources of error" },
  { icon: Award,           hex: "#F0A831", t: "Draw evidence-based conclusions" },
  { icon: GraduationCap,   hex: "#E14C7A", t: "Prepare a professional research report" },
];

const SENSORS = [
  { icon: Activity,     t: "Motion, Force & Acceleration" },
  { icon: Thermometer,  t: "Pressure & Temperature" },
  { icon: FlaskConical, t: "pH & Conductivity" },
  { icon: Zap,          t: "Voltage, Current & Magnetic Fields" },
  { icon: Waves,        t: "Light, Sound & Colour" },
  { icon: Microscope,   t: "Dissolved O₂, CO₂ & Respiration" },
];

export default function Research() {
  return (
    <div className="bg-[#050B14] text-onink" data-testid="research-page">
      <PageHero
        n="R"
        label="High School Research Program"
        lines={["Research Like a Scientist.", "Discover Like an Innovator."]}
        intro="Future scientists, doctors, engineers and innovators are not created by memorising textbooks. They are developed by asking meaningful questions, designing experiments, collecting data, and drawing evidence-based conclusions."
      />

      {/* A · Intro editorial + first video */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 mb-6">
              <Microscope className="w-4 h-4 text-accent" />
              <span className="font-mono-label text-[11px] tracking-label uppercase text-white/80">Integral part of the NextGen Training Program</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight font-light">
              From learners of science → to <span className="text-accent">contributors to science</span>.
            </h2>
            <p className="text-white/75 leading-relaxed mt-6">
              Selected NTP students participate in a structured High School Research Program that introduces them to authentic scientific research from an early stage. Under the guidance of experienced <span className="text-white">researchers and subject experts</span>, they undertake carefully designed projects in Physics, Chemistry, Biology, Mathematics, Environmental Science and interdisciplinary STEM fields.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <div className="aspect-[9/16] max-w-xs sm:max-w-sm mx-auto overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl shadow-black/40" data-testid="research-hero-video">
              <YouTubeEmbed id={VIDEOS[0].id} title={VIDEOS[0].label} short />
            </div>
          </Reveal>
        </div>
      </section>

      {/* B · Vernier / laboratory equipment */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="research-vernier">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">International-Standard Laboratory Equipment</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-5xl font-light">
            Research-grade sensors from <span className="text-accent">Vernier</span> — the same instruments used in university laboratories.
          </h2>
          <div className="grid md:grid-cols-12 gap-10 mt-12 items-start">
            <Reveal className="md:col-span-7">
              <p className="text-white/75 leading-relaxed">
                The quality of scientific research depends on the quality of measurement. Our laboratories are equipped with <span className="text-white">professional research-grade data-acquisition systems</span> and precision sensors from Vernier — one of the world's leading manufacturers of educational scientific instrumentation.
              </p>
              <p className="text-white/70 leading-relaxed mt-4">
                These internationally recognised sensors enable students to collect accurate, real-time experimental data across a wide range of investigations — moving <span className="text-white">beyond observation into quantitative inquiry</span> using methods similar to those employed in university and industry laboratories.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                {SENSORS.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <Reveal key={s.t} delay={(i % 6) * 0.04} className="flex items-center gap-3 border border-white/10 rounded-xl bg-[#050B14] px-4 py-3">
                      <Icon className="w-5 h-5 text-accent shrink-0" />
                      <span className="text-white/80 text-sm md:text-base">{s.t}</span>
                    </Reveal>
                  );
                })}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-5">
              <div className="aspect-[9/16] max-w-xs sm:max-w-sm mx-auto overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl shadow-black/40" data-testid="research-video-2">
                <YouTubeEmbed id={VIDEOS[1].id} title={VIDEOS[1].label} short />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* C · Complete research process — the 10 steps */}
      <section className="border-t border-white/10" data-testid="research-process">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="B">The Complete Scientific Research Cycle</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Students don't just perform experiments — they experience the <span className="text-accent">complete research process</span>.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            Every project is designed to develop the skills required for genuine scientific investigation. From framing a problem to publishing a conclusion, students train in the same discipline expected at leading universities and research institutions.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-14" data-testid="research-cycle-grid">
            {RESEARCH_CYCLE.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal
                  key={s.t}
                  delay={(i % 5) * 0.05}
                  className="relative border border-white/10 rounded-xl p-5 bg-[#070d18] hover:border-accent/60 transition-colors overflow-hidden group"
                  data-testid={`research-cycle-${i + 1}`}
                >
                  <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-3xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ backgroundColor: s.hex }} />
                  <div className="relative">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-label text-[10px] tracking-label uppercase" style={{ color: s.hex }}>Step {String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center border mt-3" style={{ borderColor: s.hex, backgroundColor: `${s.hex}22` }}>
                      <Icon className="w-5 h-5" style={{ color: s.hex }} />
                    </div>
                    <p className="text-white text-sm md:text-base font-display mt-3 tracking-tight leading-snug">{s.t}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <p className="text-white/60 leading-relaxed mt-12 max-w-3xl italic">
            This systematic approach develops <span className="not-italic text-white">scientific thinking, analytical ability, critical reasoning, creativity and independent problem-solving</span>.
          </p>
        </div>
      </section>

      {/* D · Widescreen video reel — inside a research lab */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="research-reel">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="C">Inside a THECREATOR Research Lab</SectionLabel>
          <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight max-w-3xl font-light">
            See the <span className="text-accent">process, precision and discipline</span> of a real high-school research project.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12" data-testid="research-video-reel">
            {VIDEOS.slice(2).map((v, i) => (
              <Reveal key={v.id} delay={i * 0.08} className="rounded-2xl overflow-hidden border border-white/10 bg-black">
                <div className="aspect-video">
                  <YouTubeEmbed id={v.id} title={v.label} />
                </div>
                <div className="p-4">
                  <div className="font-mono-label text-[10px] tracking-label uppercase text-accent">Reel · {String(i + 1).padStart(2, "0")}</div>
                  <p className="text-white/80 text-sm mt-2 leading-snug">{v.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* E · From lab to publication */}
      <section className="border-t border-white/10" data-testid="research-publication">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <SectionLabel n="D">From School Laboratory to Scientific Publication</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              Research that meets <span className="text-accent">recognised academic standards</span>.
            </h2>
            <p className="text-white/75 leading-relaxed mt-6">
              The objective is not merely to complete laboratory activities. Outstanding projects are refined under faculty mentorship and developed into research papers suitable for <span className="text-white">science fairs, innovation competitions, student research conferences</span> and — where appropriate — publication in academic research journals.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              Through this process, students experience how scientific knowledge is created and communicated — transforming from learners of science into <span className="text-white">contributors to science</span>.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="grid grid-cols-2 gap-3">
              {["Science Fairs", "Innovation Competitions", "Student Research Conferences", "Academic Journals"].map((v, i) => (
                <div key={v} className="rounded-xl border border-white/10 bg-[#070d18] p-5 flex items-center gap-3">
                  <span className="font-mono-label text-[10px] tracking-label uppercase text-accent">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-lg text-white tracking-tight leading-snug">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* F · Building future researchers — closing */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="research-future">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="✦">Building Future Researchers</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-5xl font-light">
            Beyond the classroom — students apply their knowledge to <span className="text-accent">real scientific questions</span>.
          </h2>
          <p className="text-white/75 leading-relaxed mt-6 max-w-3xl">
            This program reflects the core philosophy of the PBL-TDLE Framework: concepts are developed through investigation, experiments and problem solving. Students in the NTP Research Program develop not only stronger conceptual understanding and problem-solving ability, but also the <span className="text-white">research mindset expected at the world's leading universities and research institutions</span>.
          </p>

          <Reveal delay={0.05} className="mt-12 border-l-4 border-accent bg-[#050B14] rounded-2xl p-8 md:p-10 max-w-5xl">
            <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Our Vision</div>
            <p className="font-display text-2xl md:text-4xl text-white mt-4 leading-tight tracking-tight">
              To prepare students who can qualify for the world's most competitive academic programs, contribute to scientific innovation, and <span className="text-accent">create knowledge that benefits society</span>.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/ntp" className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-5 py-2.5 rounded-full text-sm hover:border-accent hover:text-accent transition-colors" data-testid="research-cta-ntp">
              Explore NextGen Training Program <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/enroll" className="inline-flex items-center gap-2 bg-accent text-[#050B14] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-accent/90 transition-colors" data-testid="research-cta-enroll">
              Enroll <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
